import { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Search, ChevronUp, ChevronDown, ArrowRight, Info, MessageSquare, X } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to format API error details (especially FastAPI validation errors)
function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [firms, setFirms] = useState([]);
  const [filteredFirms, setFilteredFirms] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Timesheet warning state (for POCs)
  const [unsubmittedPastDates, setUnsubmittedPastDates] = useState([]);
  const [showTimesheetWarning, setShowTimesheetWarning] = useState(false);
  
  // Initialize filters from URL query parameters (for persistence across navigation)
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [dealOwnerFilter, setDealOwnerFilter] = useState(searchParams.get('dealOwner') || 'all');
  const [cohortFilter, setCohortFilter] = useState(searchParams.get('cohort') || 'all');
  const [sortField, setSortField] = useState(searchParams.get('sortField') || 'health_score');
  const [sortOrder, setSortOrder] = useState(searchParams.get('sortOrder') || 'desc');
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [setupModalFirm, setSetupModalFirm] = useState(null); // For setup confirmation modal
  const [isUpdatingSetup, setIsUpdatingSetup] = useState(false);
  const [extendedModalFirm, setExtendedModalFirm] = useState(null); // For extended confirmation modal
  const [isUpdatingExtended, setIsUpdatingExtended] = useState(false);
  const [followUpsModalFirm, setFollowUpsModalFirm] = useState(null); // For follow-ups modal
  const [followUpsLoading, setFollowUpsLoading] = useState(false);
  const [firmFollowUps, setFirmFollowUps] = useState([]);
  const [showAddFollowUpForm, setShowAddFollowUpForm] = useState(false);
  const [newFollowUpNote, setNewFollowUpNote] = useState('');
  const [newFollowUpOutcome, setNewFollowUpOutcome] = useState('interested');
  const [newFollowUpNextDate, setNewFollowUpNextDate] = useState('');
  const [submittingFollowUp, setSubmittingFollowUp] = useState(false);
  const [followUpError, setFollowUpError] = useState('');

  const fetchFirms = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/firms`, {
        withCredentials: true
      });
      setFirms(data);
    } catch (error) {
      console.error('Failed to fetch firms:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchUnsubmittedPastDates = async () => {
    try {
      // Get today's date in IST
      const now = new Date();
      const istOffset = 5.5 * 60 * 60 * 1000;
      const istToday = new Date(now.getTime() + istOffset).toISOString().split('T')[0];
      
      // Fetch all entries for the POC
      const { data } = await axios.get(`${API_URL}/api/timesheet`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      // Find all dates with unsubmitted entries that are before today
      const pastUnsubmittedDates = new Set();
      data.entries.forEach(entry => {
        if (!entry.submitted && entry.date < istToday) {
          pastUnsubmittedDates.add(entry.date);
        }
      });
      
      const sortedDates = Array.from(pastUnsubmittedDates).sort();
      setUnsubmittedPastDates(sortedDates);
      setShowTimesheetWarning(sortedDates.length > 0);
    } catch (error) {
      console.error('Failed to fetch unsubmitted timesheet dates:', error);
    }
  };

  useEffect(() => {
    fetchFirms();
    if (user.role === 'poc') {
      fetchUnsubmittedPastDates();
    }
  }, [fetchFirms, user.role]);

  // Sync filter state to URL query parameters for persistence
  useEffect(() => {
    const params = new URLSearchParams();
    
    if (searchQuery) params.set('search', searchQuery);
    if (dealOwnerFilter !== 'all') params.set('dealOwner', dealOwnerFilter);
    if (cohortFilter !== 'all') params.set('cohort', cohortFilter);
    if (sortField !== 'health_score') params.set('sortField', sortField);
    if (sortOrder !== 'desc') params.set('sortOrder', sortOrder);
    
    setSearchParams(params, { replace: true });
  }, [searchQuery, dealOwnerFilter, cohortFilter, sortField, sortOrder, setSearchParams]);

  const applyFilters = useCallback(() => {
    let filtered = [...firms];

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(f => 
        f.firm_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        f.site_url.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Deal owner filter (match by POC name)
    if (dealOwnerFilter !== 'all') {
      filtered = filtered.filter(f => {
        const supportName = extractPocName(f.support_poc);
        const salesName = extractPocName(f.sales_poc);
        return supportName === dealOwnerFilter || salesName === dealOwnerFilter;
      });
    }

    // Cohort filter
    if (cohortFilter !== 'all') {
      filtered = filtered.filter(f => f.cohort === cohortFilter);
    }

    // Sort
    filtered.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      
      if (aVal == null) aVal = sortOrder === 'desc' ? -Infinity : Infinity;
      if (bVal == null) bVal = sortOrder === 'desc' ? -Infinity : Infinity;
      
      if (sortOrder === 'desc') {
        return bVal > aVal ? 1 : -1;
      } else {
        return aVal > bVal ? 1 : -1;
      }
    });

    setFilteredFirms(filtered);
  }, [firms, searchQuery, dealOwnerFilter, cohortFilter, sortField, sortOrder]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  const getHealthScoreColor = (score) => {
    if (score >= 80) return 'text-[#047857] bg-green-50';
    if (score >= 60) return 'text-[#0C9168] bg-green-100';
    if (score >= 40) return 'text-[#F59E0B] bg-yellow-50';
    return 'text-[#DC2626] bg-red-50';
  };

  const getTrendBadgeStyle = (trend) => {
    switch (trend) {
      case 'Zero Activity':
        return 'bg-red-50 text-red-900 border-red-900';
      case 'Significant Increase':
        return 'bg-green-900 text-white border-green-900';
      case 'Increase':
        return 'bg-green-100 text-green-900 border-green-900';
      case 'Same':
        return 'bg-[#F4F4F5] text-[#52525B] border-[#52525B]';
      case 'Decrease':
        return 'bg-orange-100 text-orange-900 border-orange-900';
      case 'New':
        return 'bg-blue-100 text-blue-900 border-blue-900';
      default:
        return 'bg-[#F4F4F5] text-[#52525B] border-[#52525B]';
    }
  };

  const handleSetupToggle = (firm, newStatus) => {
    // All users (Super Admin, Admin, POC) can update setup status
    // Show confirmation modal
    setSetupModalFirm({ ...firm, newSetupStatus: newStatus });
  };

  const confirmSetupUpdate = async () => {
    if (!setupModalFirm) return;
    
    setIsUpdatingSetup(true);
    try {
      await axios.patch(
        `${API_URL}/api/firms/${encodeURIComponent(setupModalFirm.site_url)}/setup`,
        { setup_done: setupModalFirm.newSetupStatus },
        { withCredentials: true }
      );
      
      // Update local state
      setFirms(prevFirms => 
        prevFirms.map(f => 
          f.site_url === setupModalFirm.site_url 
            ? { ...f, setup_done: setupModalFirm.newSetupStatus }
            : f
        )
      );
      
      setSetupModalFirm(null);
    } catch (error) {
      console.error('Failed to update setup status:', error);
      alert(error.response?.data?.detail || 'Failed to update setup status');
    } finally {
      setIsUpdatingSetup(false);
    }
  };

  const handleExtendedToggle = (firm, newStatus) => {
    // All users (Super Admin, Admin, POC) can update trial extension status
    // Show confirmation modal
    setExtendedModalFirm({ ...firm, newExtendedStatus: newStatus });
  };

  const confirmExtendedUpdate = async () => {
    if (!extendedModalFirm) return;
    
    setIsUpdatingExtended(true);
    try {
      await axios.patch(
        `${API_URL}/api/firms/${encodeURIComponent(extendedModalFirm.site_url)}/extended`,
        { is_extended: extendedModalFirm.newExtendedStatus },
        { withCredentials: true }
      );
      
      // Update local state
      setFirms(prevFirms => 
        prevFirms.map(f => 
          f.site_url === extendedModalFirm.site_url 
            ? { ...f, is_extended: extendedModalFirm.newExtendedStatus }
            : f
        )
      );
      
      setExtendedModalFirm(null);
    } catch (error) {
      console.error('Failed to update extended status:', error);
      alert(error.response?.data?.detail || 'Failed to update extended status');
    } finally {
      setIsUpdatingExtended(false);
    }
  };

  const getRowBackgroundClass = (firm) => {
    // Highlight based on latest follow-up outcome
    if (!firm.latest_follow_up_outcome) {
      return 'hover:bg-[#FAFAFA]'; // No follow-up = default
    }
    
    switch (firm.latest_follow_up_outcome) {
      case 'interested':
        return 'bg-green-50 hover:bg-green-100';
      case 'purchased':
        return 'bg-green-200 hover:bg-green-300';
      case 'extended':
        return 'bg-yellow-50 hover:bg-yellow-100';
      case 'cold':
        return 'bg-blue-50 hover:bg-blue-100';
      case 'deactivated':
        return 'bg-red-50 hover:bg-red-100';
      case 'no_response':
      case 'other':
        return 'bg-gray-50 hover:bg-gray-100';
      default:
        return 'hover:bg-[#FAFAFA]';
    }
  };

  // Check if any filters are active
  const hasActiveFilters = () => {
    return searchQuery !== '' || 
           dealOwnerFilter !== 'all' || 
           cohortFilter !== 'all' ||
           sortField !== 'health_score' ||
           sortOrder !== 'desc';
  };

  // Clear all filters
  const handleClearFilters = () => {
    setSearchQuery('');
    setDealOwnerFilter('all');
    setCohortFilter('all');
    setSortField('health_score');
    setSortOrder('desc');
  };
  
  const getOutcomeBadgeStyle = (outcome) => {
    switch (outcome) {
      case 'interested':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'purchased':
        return 'bg-green-900 text-white border-green-900';
      case 'extended':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'cold':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'deactivated':
        return 'bg-red-100 text-red-800 border-red-300';
      case 'no_response':
        return 'bg-gray-100 text-gray-800 border-gray-300';
      case 'other':
        return 'bg-gray-100 text-gray-800 border-gray-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };
  
  const formatOutcomeLabel = (outcome) => {
    const labels = {
      'interested': 'Interested',
      'cold': 'Cold',
      'purchased': 'Purchased',
      'no_response': 'No Response',
      'extended': 'Extended',
      'deactivated': 'Deactivated',
      'other': 'Other'
    };
    return labels[outcome] || outcome;
  };
  
  const handleViewFollowUps = async (e, firm) => {
    e.stopPropagation();
    setFollowUpsModalFirm(firm);
    setFollowUpsLoading(true);
    setShowAddFollowUpForm(false); // Reset form visibility
    setNewFollowUpNote('');
    setNewFollowUpOutcome('interested');
    setNewFollowUpNextDate('');
    setFollowUpError('');
    
    try {
      const { data } = await axios.get(`${API_URL}/api/firms/${encodeURIComponent(firm.site_url)}`, {
        withCredentials: true
      });
      setFirmFollowUps(data.follow_ups || []);
    } catch (error) {
      console.error('Failed to fetch follow-ups:', error);
      setFirmFollowUps([]);
    } finally {
      setFollowUpsLoading(false);
    }
  };
  
  const handleSubmitFollowUp = async (e) => {
    e.preventDefault();
    setSubmittingFollowUp(true);
    setFollowUpError('');
    
    try {
      await axios.post(
        `${API_URL}/api/firms/${encodeURIComponent(followUpsModalFirm.site_url)}/follow-ups`,
        {
          note: newFollowUpNote,
          outcome: newFollowUpOutcome,
          next_follow_up_date: newFollowUpNextDate || null
        },
        { withCredentials: true }
      );
      
      // Refresh follow-ups list
      const { data } = await axios.get(`${API_URL}/api/firms/${encodeURIComponent(followUpsModalFirm.site_url)}`, {
        withCredentials: true
      });
      setFirmFollowUps(data.follow_ups || []);
      
      // Reset form
      setShowAddFollowUpForm(false);
      setNewFollowUpNote('');
      setNewFollowUpOutcome('interested');
      setNewFollowUpNextDate('');
      
      // Refresh main firms list to update row highlighting
      fetchFirms();
    } catch (error) {
      console.error('Failed to add follow-up:', error);
      const errorMessage = error.response?.data?.detail 
        ? formatApiErrorDetail(error.response.data.detail)
        : 'Failed to add follow-up. Please try again.';
      setFollowUpError(errorMessage);
    } finally {
      setSubmittingFollowUp(false);
    }
  };

  const extractPocName = (email) => {
    if (!email) return null;
    // Extract the part before @ and capitalize first letter
    const name = email.split('@')[0];
    return name.charAt(0).toUpperCase() + name.slice(1);
  };

  const formatDealOwners = (supportPoc, salesPoc) => {
    const supportName = extractPocName(supportPoc);
    const salesName = extractPocName(salesPoc);
    
    if (supportName && salesName) {
      return `${supportName} & ${salesName}`;
    } else if (supportName) {
      return supportName;
    } else if (salesName) {
      return salesName;
    }
    return '—';
  };


  const formatFollowUpDate = (dateStr) => {
    if (!dateStr) return '—';
    const date = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (date < today) {
      return <span className="text-[#DC2626] font-medium">{date.toLocaleDateString()}</span>;
    } else if (date.toDateString() === today.toDateString()) {
      return <span className="text-[#EA580C] font-medium">{date.toLocaleDateString()}</span>;
    } else {
      return <span className="text-[#52525B]">{date.toLocaleDateString()}</span>;
    }
  };

  const extractSubdomain = (url) => {
    try {
      const match = url.match(/\/\/([^\.]+)\./);
      return match ? match[1] : url;
    } catch {
      return url;
    }
  };

  // Extract unique POC names for filter dropdown
  const dealOwners = Array.from(
    new Set(
      firms.flatMap(f => {
        const names = [];
        if (f.support_poc) names.push(extractPocName(f.support_poc));
        if (f.sales_poc) names.push(extractPocName(f.sales_poc));
        return names;
      }).filter(Boolean)
    )
  ).sort();

  // Calculate cohort stats from firms filtered by deal owner + search ONLY (not cohort)
  // This ensures counters always reflect the active deal owner filter
  const filteredCohortStats = (() => {
    // First, filter firms by deal owner and search (NOT cohort)
    let baseFiltered = [...firms];
    
    if (searchQuery) {
      baseFiltered = baseFiltered.filter(f => 
        f.firm_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        f.site_url.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    if (dealOwnerFilter !== 'all') {
      baseFiltered = baseFiltered.filter(f => {
        const supportName = extractPocName(f.support_poc);
        const salesName = extractPocName(f.sales_poc);
        return supportName === dealOwnerFilter || salesName === dealOwnerFilter;
      });
    }
    
    // Count firms per cohort from baseFiltered (deal owner scoped)
    const cohortCounts = {
      steady: baseFiltered.filter(f => f.cohort === 'steady').length,
      experimenter: baseFiltered.filter(f => f.cohort === 'experimenter').length,
      unstable: baseFiltered.filter(f => f.cohort === 'unstable').length,
      idle: baseFiltered.filter(f => f.cohort === 'idle').length
    };
    
    return {
      cohorts: [
        { id: "steady", label: "🎯 Steady Users", count: cohortCounts.steady, color: "#0C9168" },
        { id: "experimenter", label: "🎢 Experimenters", count: cohortCounts.experimenter, color: "#F59E0B" },
        { id: "unstable", label: "⚠️ Unstable", count: cohortCounts.unstable, color: "#DC2626" },
        { id: "idle", label: "💤 Idle", count: cohortCounts.idle, color: "#6B7280" }
      ],
      total: baseFiltered.length  // Always scoped to deal owner + search
    };
  })();

  const stats = {
    totalActive: filteredFirms.length,
    zeroActivity: filteredFirms.filter(f => f.trend_label === 'Zero Activity').length,
    overdueFollowUps: filteredFirms.filter(f => {
      if (!f.next_follow_up_date) return false;
      const followUpDate = new Date(f.next_follow_up_date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return followUpDate < today;
    }).length,
    syncedToday: firms.length // All firms are synced when fetched
  };

  return (
    <Layout>
      <div className="max-w-[1800px] mx-auto">
        {/* Timesheet Warning Banner (POC only) */}
        {user.role === 'poc' && showTimesheetWarning && (
          <div className="bg-orange-50 border-l-4 border-orange-500 p-4 mb-6">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-orange-500 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <div>
                  <p className="text-sm font-bold text-orange-900 mb-1">
                    ⏰ Unsubmitted Timesheet Entries
                  </p>
                  <p className="text-sm text-orange-800">
                    You have unsubmitted entries for {unsubmittedPastDates.length} past {unsubmittedPastDates.length === 1 ? 'date' : 'dates'}: 
                    <strong className="ml-1">{unsubmittedPastDates.slice(0, 3).join(', ')}</strong>
                    {unsubmittedPastDates.length > 3 && <span> and {unsubmittedPastDates.length - 3} more</span>}
                  </p>
                  <button
                    onClick={() => navigate('/timesheet?unsubmitted=true')}
                    className="mt-2 text-sm font-medium text-orange-900 underline hover:text-orange-700"
                  >
                    Go to Timesheet →
                  </button>
                </div>
              </div>
              <button
                onClick={() => setShowTimesheetWarning(false)}
                className="text-orange-500 hover:text-orange-700"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        )}
        
        {/* Summary Cards - Cohort Filters */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-bold uppercase tracking-[0.1em] text-[#09090B]">Trial Customer Management</h2>
            <button
              onClick={() => setShowInfoModal(true)}
              className="flex items-center gap-2 text-sm text-[#0C9168] hover:text-[#097352] font-medium"
            >
              <Info className="w-4 h-4" />
              Classification Rules
            </button>
          </div>
          
          {!loading && filteredCohortStats && (
            <div className="grid grid-cols-5 gap-1 bg-[#E4E4E7] border border-[#E4E4E7]">
              {/* All Customers Box */}
              <button
                onClick={() => setCohortFilter('all')}
                className={`bg-white p-4 text-left transition-all hover:shadow-md ${
                  cohortFilter === 'all' ? 'ring-2 ring-[#09090B] ring-inset' : ''
                }`}
                data-testid="cohort-all"
              >
                <div className="text-xs font-bold uppercase tracking-[0.1em] text-[#52525B] mb-1">
                  All Customers
                </div>
                <div className="text-2xl font-bold font-mono text-[#09090B]">
                  {filteredCohortStats.total}
                </div>
                {cohortFilter === 'all' && (
                  <div className="text-xs text-[#09090B] font-medium mt-1">
                    ✓ SHOWING ALL
                  </div>
                )}
              </button>
              
              {/* 4 Cohort Boxes */}
              {filteredCohortStats.cohorts.map(cohort => {
                // When this cohort is selected, show the actual filtered count
                const displayCount = cohortFilter === cohort.id 
                  ? filteredFirms.length  // Actual filtered count (deal owner + cohort)
                  : cohort.count;  // Base count (deal owner only)
                
                return (
                  <button
                    key={cohort.id}
                    onClick={() => setCohortFilter(cohortFilter === cohort.id ? 'all' : cohort.id)}
                    className={`bg-white p-4 text-left transition-all hover:shadow-md ${
                      cohortFilter === cohort.id ? 'ring-2 ring-[#0C9168] ring-inset' : ''
                    }`}
                    data-testid={`cohort-${cohort.id}`}
                  >
                    <div className="text-xs font-bold uppercase tracking-[0.1em] text-[#52525B] mb-1">
                      {cohort.label}
                    </div>
                    <div className="text-2xl font-bold font-mono" style={{color: cohort.color}}>
                      {displayCount}
                    </div>
                    {cohortFilter === cohort.id && (
                      <div className="text-xs text-[#0C9168] font-medium mt-1">
                        ✓ FILTERED
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Filters */}
        <div className="bg-white border border-[#E4E4E7] p-4 mb-6">
          <div className="flex gap-4 flex-wrap items-center">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#52525B]" strokeWidth={1.5} />
                <input
                  type="text"
                  placeholder="Search by firm name or URL"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  data-testid="search-input"
                  className="w-full pl-10 pr-4 py-2 border border-[#E4E4E7] bg-white text-[#09090B] focus:outline-none focus:ring-2 focus:ring-[#0C9168] text-sm"
                />
              </div>
            </div>
            <select
              value={dealOwnerFilter}
              onChange={(e) => setDealOwnerFilter(e.target.value)}
              data-testid="deal-owner-filter"
              className="px-4 py-2 border border-[#E4E4E7] bg-white text-[#09090B] focus:outline-none focus:ring-2 focus:ring-[#0C9168] text-sm"
            >
              <option value="all">All Deal Owners</option>
              {dealOwners.map(owner => (
                <option key={owner} value={owner}>{owner}</option>
              ))}
            </select>
            
            {/* Clear Filters Button - Only show when filters are active */}
            {hasActiveFilters() && (
              <button
                onClick={handleClearFilters}
                data-testid="clear-filters-btn"
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-[#71717A] hover:text-[#18181B] transition-colors"
              >
                <X className="w-4 h-4" strokeWidth={2} />
                Clear Filters
              </button>
            )}
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="bg-white border border-[#E4E4E7] p-8 text-center text-[#52525B]">
            Loading firms...
          </div>
        ) : (
          <div className="bg-white border border-[#E4E4E7]">
            <table className="w-full border-collapse" data-testid="firms-table">
              <thead>
                <tr className="bg-[#F4F4F5] border-b border-[#E4E4E7]">
                  <th className="text-left text-xs uppercase tracking-wider font-bold p-3 cursor-pointer" onClick={() => handleSort('firm_name')}>
                    <div className="flex items-center gap-1">
                      Firm Name
                      {sortField === 'firm_name' && (sortOrder === 'desc' ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />)}
                    </div>
                  </th>
                  <th className="text-left text-xs uppercase tracking-wider font-bold p-3 cursor-pointer" onClick={() => handleSort('health_score')}>
                    <div className="flex items-center gap-1">
                      Health
                      {sortField === 'health_score' && (sortOrder === 'desc' ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />)}
                    </div>
                  </th>
                  <th className="text-left text-xs uppercase tracking-wider font-bold p-3">Cohort</th>
                  <th className="text-left text-xs uppercase tracking-wider font-bold p-3 cursor-pointer" onClick={() => handleSort('allocated_on')}>
                    <div className="flex items-center gap-1">
                      Allocated On
                      {sortField === 'allocated_on' && (sortOrder === 'desc' ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />)}
                    </div>
                  </th>
                  <th className="text-left text-xs uppercase tracking-wider font-bold p-3 cursor-pointer" onClick={() => handleSort('days_used_30d')}>
                    <div className="flex items-center gap-1">
                      Days Used
                      {sortField === 'days_used_30d' && (sortOrder === 'desc' ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />)}
                    </div>
                  </th>
                  <th className="text-left text-xs uppercase tracking-wider font-bold p-3">Silent For</th>
                  <th className="text-left text-xs uppercase tracking-wider font-bold p-3 cursor-pointer" onClick={() => handleSort('events_30d')}>
                    <div className="flex items-center gap-1">
                      Events (30d)
                      {sortField === 'events_30d' && (sortOrder === 'desc' ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />)}
                    </div>
                  </th>
                  <th className="text-left text-xs uppercase tracking-wider font-bold p-3">Deal Owner</th>
                  <th className="text-left text-xs uppercase tracking-wider font-bold p-3">Setup</th>
                  <th className="text-left text-xs uppercase tracking-wider font-bold p-3">Extd?</th>
                  <th className="w-16 p-3"></th>
                </tr>
              </thead>
              <tbody>
                {filteredFirms.map((firm) => (
                  <tr
                    key={firm.site_url}
                    className={`border-b border-[#E4E4E7] transition-colors cursor-pointer ${getRowBackgroundClass(firm)}`}
                    onClick={() => navigate(`/firm/${encodeURIComponent(firm.site_url)}`)}
                    data-testid="firm-row"
                  >
                    {/* Firm Name */}
                    <td className="p-3 text-sm text-[#09090B] font-medium">
                      <div className="flex flex-col">
                        <div className="flex items-center gap-2">
                          <span>{firm.firm_name}</span>
                          <ArrowRight className="w-3 h-3 text-[#52525B]" strokeWidth={1.5} />
                        </div>
                        <span className="text-xs text-[#6B7280] font-normal mt-0.5">
                          {firm.site_url}
                        </span>
                      </div>
                    </td>
                    
                    {/* Health Score */}
                    <td className="p-3">
                      <span className={`inline-flex items-center justify-center w-12 h-7 text-xs font-bold rounded ${
                        firm.health_score >= 80 ? 'bg-green-100 text-green-800' :
                        firm.health_score >= 60 ? 'bg-[#D1FAE5] text-[#0C9168]' :
                        firm.health_score >= 40 ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {firm.health_score || 0}
                      </span>
                    </td>
                    
                    {/* Cohort */}
                    <td className="p-3">
                      <span className={`inline-flex items-center px-2 py-0.5 text-xs font-semibold border ${
                        firm.cohort === 'champion' ? 'bg-green-50 text-green-800 border-green-300' :
                        firm.cohort === 'steady' ? 'bg-[#D1FAE5] text-[#0C9168] border-[#0C9168]' :
                        firm.cohort === 'slow_starter' ? 'bg-yellow-50 text-yellow-800 border-yellow-300' :
                        firm.cohort === 'experimenter' ? 'bg-orange-50 text-orange-700 border-orange-300' :
                        firm.cohort === 'unstable' ? 'bg-red-50 text-red-700 border-red-300' :
                        'bg-gray-50 text-gray-700 border-gray-300'
                      }`}>
                        {firm.cohort_info?.label || 'Unknown'}
                      </span>
                    </td>
                    
                    {/* Allocated On */}
                    <td className="p-3 text-sm text-[#52525B]">
                      {firm.allocated_on ? new Date(firm.allocated_on).toLocaleDateString('en-US', { 
                        year: 'numeric', 
                        month: 'short', 
                        day: 'numeric' 
                      }) : '—'}
                    </td>
                    
                    {/* Days Used (30 days) */}
                    <td className="p-3 text-sm text-[#09090B] font-mono">
                      {firm.days_used_30d || 0}
                    </td>
                    
                    {/* Silent For (days since last activity) */}
                    <td className="p-3 text-sm font-mono text-[#52525B]">
                      {firm.silent_for != null ? (
                        firm.silent_for === 0 ? (
                          <span className="text-[#0C9168]">Active today</span>
                        ) : firm.silent_for === 1 ? (
                          '1 day'
                        ) : (
                          `${firm.silent_for} days`
                        )
                      ) : '—'}
                    </td>
                    
                    {/* Events (30d) */}
                    <td className="p-3 text-sm text-[#09090B] font-mono font-bold">{firm.events_30d || 0}</td>
                    
                    {/* Deal Owner */}
                    <td className="p-3 text-sm text-[#09090B]">{formatDealOwners(firm.support_poc, firm.sales_poc)}</td>
                    
                    {/* Setup */}
                    <td className="p-3 text-sm" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => handleSetupToggle(firm, !firm.setup_done)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#0C9168] focus:ring-offset-2 ${
                          firm.setup_done ? 'bg-[#0C9168]' : 'bg-gray-300'
                        }`}
                        title={firm.setup_done ? 'Setup complete - Click to mark incomplete' : 'Setup incomplete - Click to mark complete'}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            firm.setup_done ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </td>
                    
                    {/* Extended */}
                    <td className="p-3 text-sm" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => handleExtendedToggle(firm, !firm.is_extended)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#0C9168] focus:ring-offset-2 ${
                          firm.is_extended ? 'bg-[#0C9168]' : 'bg-gray-300'
                        }`}
                        title={firm.is_extended ? 'Trial extended - Click to mark not extended' : 'Trial not extended - Click to mark extended'}
                        data-testid="extended-toggle-btn"
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            firm.is_extended ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </td>
                    
                    {/* Follow-ups */}
                    <td className="p-2 w-16" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={(e) => handleViewFollowUps(e, firm)}
                        className="p-2 hover:bg-gray-200 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#0C9168]"
                        title="View follow-ups"
                      >
                        <MessageSquare className="w-4 h-4 text-[#52525B]" strokeWidth={1.5} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredFirms.length === 0 && (
              <div className="p-8 text-center text-[#52525B]">
                No firms found matching your filters.
              </div>
            )}
          </div>
        )}
      </div>

      {/* Info Modal */}
      {showInfoModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowInfoModal(false)}>
          <div className="bg-white max-w-3xl max-h-[80vh] overflow-y-auto p-6 m-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-[#09090B]">Trial Classification Rules</h2>
              <button onClick={() => setShowInfoModal(false)} className="text-[#52525B] hover:text-[#09090B]">×</button>
            </div>
            
            <div className="space-y-4 text-sm">
              <div className="border-l-4 border-[#0C9168] pl-4">
                <h3 className="font-bold text-[#0C9168] mb-1">🎯 Steady User (65% conversion)</h3>
                <p className="text-[#52525B]"><strong>Criteria:</strong> 30-day events ≥ 400</p>
                <p className="text-[#52525B] text-xs mt-1"><strong>Action:</strong> Regular nurturing and check-ins</p>
              </div>
              
              <div className="border-l-4 border-orange-600 pl-4">
                <h3 className="font-bold text-orange-700 mb-1">🎢 Experimenter (30% conversion)</h3>
                <p className="text-[#52525B]"><strong>Criteria:</strong> 30-day events ≥ 200 and &lt; 400</p>
                <p className="text-[#52525B] text-xs mt-1"><strong>Action:</strong> Understand use case and provide guidance</p>
              </div>
              
              <div className="border-l-4 border-red-600 pl-4">
                <h3 className="font-bold text-red-700 mb-1">⚠️ Unstable (12% conversion)</h3>
                <p className="text-[#52525B]"><strong>Criteria:</strong> 30-day events ≥ 50 and &lt; 200</p>
                <p className="text-[#52525B] text-xs mt-1"><strong>Action:</strong> Re-engagement campaign needed</p>
              </div>
              
              <div className="border-l-4 border-gray-600 pl-4">
                <h3 className="font-bold text-gray-700 mb-1">💤 Idle (5% conversion)</h3>
                <p className="text-[#52525B]"><strong>Criteria:</strong> 30-day events &lt; 50</p>
                <p className="text-[#52525B] text-xs mt-1"><strong>Action:</strong> Likely lost - minimal intervention</p>
              </div>

              <div className="mt-6 p-4 bg-[#F4F4F5] border border-[#E4E4E7]">
                <h4 className="font-bold text-[#09090B] mb-2">Classification Summary</h4>
                <p className="text-[#52525B] text-xs mb-3">
                  Firms are classified into 4 categories based purely on their 30-day event count. No complex calculations needed.
                </p>
                <ul className="space-y-2 text-[#52525B] text-xs">
                  <li><strong>Health Score:</strong> Composite score (0-100) based on event volume (50%), usage frequency (35%), and setup completion (15%)</li>
                  <li><strong>Days Used (30d):</strong> Number of days with at least 1 event in the last 30 days</li>
                  <li><strong>Silent For:</strong> Number of days since last activity (when events &gt; 0)</li>
                  <li><strong>Events (30d):</strong> Total event count in last 30 days - <strong>PRIMARY CLASSIFICATION METRIC</strong></li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Setup Confirmation Modal */}
      {setupModalFirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => !isUpdatingSetup && setSetupModalFirm(null)}>
          <div className="bg-white max-w-md w-full p-6 m-4" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-bold text-[#09090B] mb-4">
              {setupModalFirm.newSetupStatus ? 'Mark Setup as Complete?' : 'Mark Setup as Incomplete?'}
            </h2>
            
            <div className="mb-6 space-y-2">
              <div className="p-3 bg-[#F4F4F5] border border-[#E4E4E7]">
                <p className="text-sm text-[#52525B]"><strong>Firm:</strong> {setupModalFirm.firm_name}</p>
                <p className="text-sm text-[#52525B] break-all"><strong>URL:</strong> {setupModalFirm.site_url}</p>
              </div>
              
              {setupModalFirm.newSetupStatus ? (
                <p className="text-sm text-[#09090B]">
                  Confirm that the initial setup for <strong>{setupModalFirm.firm_name}</strong> has been completed?
                </p>
              ) : (
                <p className="text-sm text-[#09090B]">
                  Mark setup as incomplete for <strong>{setupModalFirm.firm_name}</strong>?
                </p>
              )}
            </div>
            
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setSetupModalFirm(null)}
                disabled={isUpdatingSetup}
                className="px-4 py-2 border border-[#E4E4E7] text-[#09090B] hover:bg-[#F4F4F5] disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmSetupUpdate}
                disabled={isUpdatingSetup}
                className="px-4 py-2 bg-[#0C9168] text-white hover:bg-[#097352] disabled:opacity-50"
              >
                {isUpdatingSetup ? 'Updating...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Extended Confirmation Modal */}
      {extendedModalFirm && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" 
          onClick={() => !isUpdatingExtended && setExtendedModalFirm(null)}
          data-testid="extended-confirm-modal"
        >
          <div className="bg-white max-w-md w-full p-6 m-4" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-bold text-[#09090B] mb-4">
              {extendedModalFirm.newExtendedStatus ? 'Mark Trial as Extended?' : 'Mark Trial as Not Extended?'}
            </h2>
            
            <div className="mb-6 space-y-2">
              <div className="p-3 bg-[#F4F4F5] border border-[#E4E4E7]">
                <p className="text-sm text-[#52525B]"><strong>Firm:</strong> {extendedModalFirm.firm_name}</p>
                <p className="text-sm text-[#52525B] break-all"><strong>URL:</strong> {extendedModalFirm.site_url}</p>
              </div>
              
              {extendedModalFirm.newExtendedStatus ? (
                <p className="text-sm text-[#09090B]">
                  Confirm that the trial period for <strong>{extendedModalFirm.firm_name}</strong> has been extended?
                </p>
              ) : (
                <p className="text-sm text-[#09090B]">
                  Mark trial as not extended for <strong>{extendedModalFirm.firm_name}</strong>?
                </p>
              )}
            </div>
            
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setExtendedModalFirm(null)}
                disabled={isUpdatingExtended}
                className="px-4 py-2 border border-[#E4E4E7] text-[#09090B] hover:bg-[#F4F4F5] disabled:opacity-50"
                data-testid="extended-cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={confirmExtendedUpdate}
                disabled={isUpdatingExtended}
                className="px-4 py-2 bg-[#0C9168] text-white hover:bg-[#097352] disabled:opacity-50"
                data-testid="extended-confirm-btn"
              >
                {isUpdatingExtended ? 'Updating...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Follow-ups Modal */}
      {followUpsModalFirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setFollowUpsModalFirm(null)}>
          <div className="bg-white max-w-3xl w-full max-h-[80vh] m-4 flex flex-col" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-[#E4E4E7]">
              <div>
                <h2 className="text-xl font-bold text-[#09090B]">Follow-ups</h2>
                <p className="text-sm text-[#52525B] mt-1">
                  {followUpsModalFirm.firm_name}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowAddFollowUpForm(!showAddFollowUpForm)}
                  className="px-4 py-2 bg-[#0C9168] text-white hover:bg-[#097352] transition-colors text-sm font-medium"
                >
                  {showAddFollowUpForm ? 'Cancel' : '+ Add Follow-up'}
                </button>
                <button 
                  onClick={() => setFollowUpsModalFirm(null)} 
                  className="text-[#52525B] hover:text-[#09090B] text-2xl font-light"
                >
                  ×
                </button>
              </div>
            </div>
            
            {/* Content */}
            <div className="overflow-y-auto p-6 flex-1">
              {/* Add Follow-up Form */}
              {showAddFollowUpForm && (
                <div className="bg-[#F9FAFB] border border-[#E4E4E7] p-6 mb-6">
                  <h3 className="text-sm font-bold uppercase tracking-[0.1em] text-[#09090B] mb-4">
                    Add New Follow-up
                  </h3>
                  <form onSubmit={handleSubmitFollowUp} className="space-y-4">
                    <div>
                      <label htmlFor="modal-note" className="block text-xs font-bold uppercase tracking-[0.1em] text-[#09090B] mb-2">
                        Note *
                      </label>
                      <textarea
                        id="modal-note"
                        value={newFollowUpNote}
                        onChange={(e) => setNewFollowUpNote(e.target.value)}
                        required
                        rows="3"
                        placeholder="What happened? What was discussed?"
                        className="w-full px-4 py-2 border border-[#E4E4E7] bg-white text-[#09090B] focus:outline-none focus:ring-2 focus:ring-[#0C9168] text-sm"
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="modal-outcome" className="block text-xs font-bold uppercase tracking-[0.1em] text-[#09090B] mb-2">
                          Outcome *
                        </label>
                        <select
                          id="modal-outcome"
                          value={newFollowUpOutcome}
                          onChange={(e) => setNewFollowUpOutcome(e.target.value)}
                          required
                          className="w-full px-4 py-2 border border-[#E4E4E7] bg-white text-[#09090B] focus:outline-none focus:ring-2 focus:ring-[#0C9168] text-sm"
                        >
                          <option value="interested">Interested</option>
                          <option value="cold">Cold</option>
                          <option value="purchased">Purchased</option>
                          <option value="no_response">No Response</option>
                          <option value="extended">Extended</option>
                          <option value="deactivated">Deactivated</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                      
                      <div>
                        <label htmlFor="modal-next-date" className="block text-xs font-bold uppercase tracking-[0.1em] text-[#09090B] mb-2">
                          Next Follow-up Date
                        </label>
                        <input
                          id="modal-next-date"
                          type="date"
                          value={newFollowUpNextDate}
                          onChange={(e) => setNewFollowUpNextDate(e.target.value)}
                          className="w-full px-4 py-2 border border-[#E4E4E7] bg-white text-[#09090B] focus:outline-none focus:ring-2 focus:ring-[#0C9168] text-sm"
                        />
                      </div>
                    </div>
                    
                    {followUpError && (
                      <div className="bg-red-50 border border-red-600 p-3 text-sm text-red-900">
                        {followUpError}
                      </div>
                    )}
                    
                    <div className="flex gap-3 pt-2">
                      <button
                        type="submit"
                        disabled={submittingFollowUp}
                        className="bg-[#0C9168] text-white hover:bg-[#097352] px-6 py-2 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                      >
                        {submittingFollowUp ? 'Adding...' : 'Add Follow-up'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowAddFollowUpForm(false)}
                        className="px-6 py-2 border border-[#E4E4E7] text-[#09090B] hover:bg-[#F4F4F5] font-medium transition-colors text-sm"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              )}
              
              {/* Follow-ups List */}
              {followUpsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-[#52525B]">Loading follow-ups...</div>
                </div>
              ) : firmFollowUps.length === 0 ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <MessageSquare className="w-12 h-12 text-[#E4E4E7] mx-auto mb-3" />
                    <p className="text-[#52525B]">No follow-ups recorded yet</p>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {firmFollowUps.map((followUp, index) => (
                    <div key={index} className="border border-[#E4E4E7] bg-white p-4">
                      {/* Header: Date and Outcome Badge */}
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <p className="text-xs text-[#52525B] mb-1">
                            {new Date(followUp.created_at).toLocaleDateString('en-US', { 
                              year: 'numeric', 
                              month: 'short', 
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                          <p className="text-xs text-[#6B7280]">
                            by {followUp.created_by_name}
                          </p>
                        </div>
                        <span className={`px-3 py-1 text-xs font-medium border ${getOutcomeBadgeStyle(followUp.outcome)}`}>
                          {formatOutcomeLabel(followUp.outcome)}
                        </span>
                      </div>
                      
                      {/* Note */}
                      <p className="text-sm text-[#09090B] mb-2">
                        {followUp.note}
                      </p>
                      
                      {/* Next Follow-up Date */}
                      {followUp.next_follow_up_date && (
                        <div className="mt-3 pt-3 border-t border-[#F4F4F5]">
                          <p className="text-xs text-[#52525B]">
                            <strong>Next follow-up:</strong> {new Date(followUp.next_follow_up_date).toLocaleDateString('en-US', { 
                              year: 'numeric', 
                              month: 'short', 
                              day: 'numeric'
                            })}
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Footer */}
            <div className="p-6 border-t border-[#E4E4E7]">
              <button
                onClick={() => navigate(`/firm/${encodeURIComponent(followUpsModalFirm.site_url)}`)}
                className="w-full px-4 py-2 bg-[#0C9168] text-white hover:bg-[#097352] transition-colors"
              >
                View Full Firm Details
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}